import { useCallback } from 'react';
import type { Control, FieldPath, FieldValues, UseControllerProps } from 'react-hook-form';
import { useController } from 'react-hook-form';

import FieldErrors from './FieldErrors';
import { WysiwygTextField } from './WysiwygTextField';

export type TWysiwygFormTextFieldProps<
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  control: Control<TFieldValues>;
  fieldPath: TFieldPath;
  rules?: UseControllerProps<TFieldValues, TFieldPath>['rules'];
  placeholder?: string;
  onBlur?: () => void;
};

export const WysiwygFormTextField = <
  TFieldValues extends FieldValues,
  TFieldPath extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  fieldPath,
  control,
  rules,
  placeholder,
  onBlur,
}: TWysiwygFormTextFieldProps<TFieldValues, TFieldPath>) => {
  const {
    field: { onBlur: innerOnBlur, onChange, ref, value },
    fieldState: { invalid, error },
  } = useController({ name: fieldPath, control, rules });

  const composedOnBlur = useCallback(() => {
    innerOnBlur();
    onBlur?.();
  }, [onBlur, innerOnBlur]);

  return (
    <div>
      <WysiwygTextField
        value={value}
        onChange={onChange}
        ref={ref}
        onBlur={composedOnBlur}
        placeholder={placeholder}
      />
      {invalid && error && <FieldErrors error={error} />}
    </div>
  );
};
